/**
 * Gráfico de linhas para evolução de Novas x Baixadas
 * Com labels mostrando o total de unidades em cada mês
 */
import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface MonthData {
  month: string;
  novas: number;
  baixadas: number;
  total?: number;
}

interface NovasBaixadasChartProps {
  data: MonthData[];
  isLoading?: boolean;
  className?: string;
  selectedYear?: number;
  onYearChange?: (year: number) => void;
}

export function NovasBaixadasChart({
  data,
  isLoading = false,
  className,
  selectedYear: controlledYear,
  onYearChange,
}: NovasBaixadasChartProps) {
  // Garante que data é um array válido
  const safeData = useMemo(() => Array.isArray(data) ? data : [], [data]);

  // Extrair anos únicos dos dados
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    safeData.forEach(d => {
      if (d.month && d.month.includes('-')) {
        const year = parseInt(d.month.split('-')[0], 10);
        if (!isNaN(year)) years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Ordem decrescente (mais recente primeiro)
  }, [safeData]);

  // Ano atual por padrão
  const currentYear = new Date().getFullYear();
  const [localYear, setLocalYear] = useState(currentYear);
  const selectedYear = controlledYear ?? localYear;
  const setSelectedYear = (year: number) => {
    setLocalYear(year);
    onYearChange?.(year);
  };

  // Filtrar dados do ano selecionado
  const yearData = useMemo(() => {
    return safeData.filter(d => {
      if (!d.month || !d.month.includes('-')) return false;
      const year = parseInt(d.month.split('-')[0], 10);
      return year === selectedYear;
    });
  }, [safeData, selectedYear]);

  // Handlers de navegação
  const canGoPrevious = availableYears.indexOf(selectedYear) < availableYears.length - 1;
  const canGoNext = availableYears.indexOf(selectedYear) > 0;

  const handlePreviousYear = () => {
    const currentIndex = availableYears.indexOf(selectedYear);
    if (currentIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[currentIndex + 1]);
    }
  };

  const handleNextYear = () => {
    const currentIndex = availableYears.indexOf(selectedYear);
    if (currentIndex > 0) {
      setSelectedYear(availableYears[currentIndex - 1]);
    }
  };

  // Formata mês para exibição (YYYY-MM -> MMM)
  const formatMonth = (month: string) => {
    if (!month || !month.includes('-')) return month;
    const [, monthNum] = month.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const idx = parseInt(monthNum, 10) - 1;
    return months[idx] || monthNum;
  };

  // Calcula totais para exibir no header (baseado no ano selecionado)
  const totalNovas = useMemo(() => yearData.reduce((sum, d) => sum + (d.novas || 0), 0), [yearData]);
  const totalBaixadas = useMemo(() => yearData.reduce((sum, d) => sum + (d.baixadas || 0), 0), [yearData]);
  const saldo = totalNovas - totalBaixadas;

  // Último total disponível
  const lastTotal = useMemo(() => {
    const lastWithTotal = [...yearData].reverse().find(d => d.total !== undefined && d.total > 0);
    return lastWithTotal?.total || 0;
  }, [yearData]);

  // Prepara os dados do chart
  const chartData = useMemo(() => {
    return yearData.map((d) => ({
      month: formatMonth(d.month),
      novas: d.novas || 0,
      baixadas: d.baixadas || 0,
      total: d.total || 0,
    }));
  }, [yearData]);

  const option: EChartsOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
      formatter: (params: any) => {
        if (!params || params.length === 0) return '';
        const monthLabel = params[0]?.axisValueLabel || '';
        const dataIndex = params[0]?.dataIndex ?? 0;
        const total = chartData[dataIndex]?.total || 0;
        
        let html = `<div style="font-weight: bold; margin-bottom: 4px;">${monthLabel}</div>`;
        html += `<div style="color: #3b82f6; margin-bottom: 2px;">Total: <strong>${total.toLocaleString('pt-BR')}</strong></div>`;
        
        params.filter((item: any) => item.seriesName !== 'Total').forEach((item: any) => {
          html += `
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${item.color};"></span>
              ${item.seriesName}: <strong>${(item.value || 0).toLocaleString('pt-BR')}</strong>
            </div>
          `;
        });
        return html;
      },
    },
    legend: {
      data: ['Novas', 'Baixadas'],
      bottom: 0,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: {
        fontSize: 11,
      },
    },
    grid: {
      left: 10,
      right: 10,
      top: 35,
      bottom: 35,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData.map((d) => d.month),
      axisLabel: {
        fontSize: 10,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 10,
        formatter: (value: number) => value.toLocaleString('pt-BR'),
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          opacity: 0.3,
        },
      },
    },
    animationDuration: 600,
    animationEasing: 'cubicInOut',
    series: [
      // Série invisível para posicionar labels de Total acima de ambas as linhas
      {
        name: 'Total',
        type: 'line',
        data: chartData.map((d) => Math.max(d.novas, d.baixadas)),
        smooth: false,
        symbol: 'none',
        lineStyle: { width: 0, opacity: 0 },
        itemStyle: { opacity: 0 },
        silent: true, // Não interage com mouse
        label: {
          show: true,
          position: 'top',
          distance: 6,
          fontSize: 10,
          fontWeight: 'bold',
          color: '#3b82f6',
          formatter: (params: any) => {
            const idx = params.dataIndex;
            const total = chartData[idx]?.total || 0;
            return total.toLocaleString('pt-BR');
          },
        },
      },
      {
        name: 'Novas',
        type: 'line',
        data: chartData.map((d) => d.novas),
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: '#10b981',
        },
        itemStyle: {
          color: '#10b981',
        },
      },
      {
        name: 'Baixadas',
        type: 'line',
        data: chartData.map((d) => d.baixadas),
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: '#ef4444',
        },
        itemStyle: {
          color: '#ef4444',
        },
      },
    ],
  }), [chartData]);

  if (isLoading) {
    return (
      <div className={cn(
        'bg-card rounded-lg border p-4 animate-pulse',
        className
      )}>
        <div className="h-4 bg-muted rounded w-40 mb-4" />
        <div className="h-[220px] bg-muted rounded" />
      </div>
    );
  }

  if (safeData.length === 0) {
    return (
      <div className={cn(
        'bg-card rounded-lg border p-4',
        className
      )}>
        <h3 className="text-sm font-medium text-muted-foreground mb-4">
          Novas x Baixadas ({currentYear})
        </h3>
        <div className="flex items-center justify-center min-h-[220px]">
          <span className="text-muted-foreground text-sm">Sem dados disponíveis</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'bg-card rounded-lg border p-4',
      className
    )}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            Novas x Baixadas
          </h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handlePreviousYear}
              disabled={!canGoPrevious}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold text-foreground min-w-[60px] text-center">
              {selectedYear}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleNextYear}
              disabled={!canGoNext}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex gap-4 text-xs">
          <span className="text-blue-600 font-medium">
            Total: {lastTotal.toLocaleString('pt-BR')}
          </span>
          <span className="text-emerald-600">
            +{totalNovas.toLocaleString('pt-BR')} novas
          </span>
          <span className="text-red-600">
            -{totalBaixadas.toLocaleString('pt-BR')} baixadas
          </span>
          <span className={saldo >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
            Saldo: {saldo >= 0 ? '+' : ''}{saldo.toLocaleString('pt-BR')}
          </span>
        </div>
      </div>
      
      <ReactECharts
        option={option}
        style={{ height: 220, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge={true}
      />
    </div>
  );
}
